import { getProducts, type Products } from '@forest/native-services'
import {
  getChinaAndroidTrialProductId,
  type ServiceLevelStatus,
} from '@forest/paywall-core'
import { isHTTPError } from 'ky'
import {
  mockScenarioConfig,
  platform,
  resolvedAppVersion,
  shouldUseMockAPI,
} from '~/lib/params'
import { timed } from '~/lib/perf'
import { userInfoSource } from '~/store/user-info'
import { api } from './api'
import { isRecord, type DisplayMetadata } from './display-metadata'

export const SHOP_COLLECTION_CODES = [
  'shop-classic',
  'shop-coin',
  'shop-gem',
  'shop-main-product',
  'shop-daily-pick',
] as const

export type ShopCollectionCode = (typeof SHOP_COLLECTION_CODES)[number]

// Collections Shop asks for but can live without: a response missing one
// leaves it undefined instead of failing bootstrap (GLOSSARY.md "Refresh
// ticket" — the refresh button hides, Shop otherwise works; "Starter pack" —
// no starter pack, ADR 0044).
export const OPTIONAL_SHOP_COLLECTION_CODES = [
  'shop-daily-refresh',
  'shop-starter-pack',
] as const

export type OptionalShopCollectionCode =
  (typeof OPTIONAL_SHOP_COLLECTION_CODES)[number]

export type ShopCollections = Record<
  ShopCollectionCode,
  ShopProductCollection
> &
  Partial<Record<OptionalShopCollectionCode, ShopProductCollection>>

export interface CurrentUser {
  id: number
  coin: number
  gem: number
}

export interface ShopProduct {
  id: number
  item_type_id: number | null
  // Always the original price; the optional sale trio yields the effective
  // price while its window contains server now (GLOSSARY.md "Effective
  // price", ADR 0027).
  price: number
  sale_price?: number | null
  sale_start_at?: string | null
  sale_end_at?: string | null
  // 'free' marks a free product (GLOSSARY.md "Free product"), whose price and
  // sale fields are never read.
  purchase_type: 'coin' | 'free' | 'gem' | 'iap'
  purchaseable: boolean
  // Main-product merchandising JSON object (GLOSSARY.md "Display metadata");
  // other collections ignore it.
  display_metadata?: DisplayMetadata | null
  // The one client-side availability gate, main products only (GLOSSARY.md
  // "Shop purchase limits"): drives the hero countdown and removes the card
  // once the Shop server clock passes it.
  purchase_end_at?: string | null
  iap_sku?: string | null
  iap_sku_ios?: string | null
  iap_sku_android?: string | null
  iap_sku_android_cn?: string | null
}

export interface ShopCollectionProduct {
  id: number
  display_order: number | null
  // The per-user deadline on a starter-pack entry only (GLOSSARY.md "Starter
  // pack"); other collections' entries never carry it.
  eligible_until?: string | null
  // shop-gem entries only: the per-user offers on the pack, of which Shop
  // reads the first-purchase one (GLOSSARY.md "First-purchase bonus
  // (首購翻倍)"). Runtime data is validated before use.
  offers?: ShopCollectionOffer[]
  item_type_product: ShopProduct | null
  item_type_product_collection_id: number
  item_type_product_id: number
}

export interface ShopCollectionOffer {
  // Never sent back: the backend applies the offer to the ordinary purchase.
  offer_id: number
  // 'first_purchase' is the one type Shop reads (readFirstPurchaseOffer);
  // others are ignored.
  offer_type: string
  eligible: boolean
  ends_at: string | null
  bonus_item_type_id: number
}

// How Shop reads an entry's `offers` for a First-purchase bonus (GLOSSARY.md
// "First-purchase bonus (首購翻倍)"): the first `first_purchase` offer.
// Bootstrap requests the bonus item type of an eligible one and currency
// hydration resolves that same one, so the two never disagree. 'none': no
// first-purchase offer, or an ineligible one.
export type FirstPurchaseOfferReading =
  | { kind: 'none' }
  | { kind: 'invalid' }
  | { kind: 'eligible'; bonusItemTypeId: number; endsAtMs: number | null }

export function readFirstPurchaseOffer(
  offers: unknown[],
): FirstPurchaseOfferReading {
  const offer = offers.find(
    (each) => isRecord(each) && each.offer_type === 'first_purchase',
  )
  if (!isRecord(offer)) {
    return { kind: 'none' }
  }
  const endsAtMs = parseEndsAt(offer.ends_at)
  const bonusItemTypeId = offer.bonus_item_type_id
  if (
    typeof offer.eligible !== 'boolean' ||
    endsAtMs === 'invalid' ||
    typeof bonusItemTypeId !== 'number' ||
    !Number.isFinite(bonusItemTypeId)
  ) {
    return { kind: 'invalid' }
  }
  if (!offer.eligible) {
    return { kind: 'none' }
  }
  return { kind: 'eligible', bonusItemTypeId, endsAtMs }
}

function parseEndsAt(value: unknown): number | null | 'invalid' {
  if (value === null) {
    return null
  }
  if (typeof value !== 'string') {
    return 'invalid'
  }
  const ms = Date.parse(value)
  return Number.isFinite(ms) ? ms : 'invalid'
}

export interface ShopProductCollection {
  code: string
  collection_type: 'static' | 'draw' | 'rotation' | 'starter_pack'
  id: number
  item_type_product_collection_products: ShopCollectionProduct[]
  name: string
}

export interface ItemContent {
  id?: number
  icon_image_url?: string
  title_key?: string
  type: string
  value: number
}

export interface ItemType {
  id: number
  icon_image_url: string
  title_key: string
  item_contents: ItemContent[]
}

export interface UnlockedTreeTimestamp {
  gid: number
  unlocked_at: string
}

// Land-skin gids are a separate namespace from tree gids (GLOSSARY.md "Owned
// land skin"); the two ownership lists never mix.
export interface UnlockedLandSkinTimestamp {
  gid: number
  unlocked_at: string
}

export interface ShopBootstrap {
  collections: ShopCollections
  currentUser: CurrentUser
  itemTypes: ItemType[]
  serviceLevelStatus: ServiceLevelStatus
  unlockedLandSkins: UnlockedLandSkinTimestamp[]
  unlockedTrees: UnlockedTreeTimestamp[]
}

export type PassivePlusState = 'try' | 'get' | 'active'

export function isInSubscription(
  serviceLevelStatus: ServiceLevelStatus,
): boolean {
  return (
    'subscription' in serviceLevelStatus &&
    Boolean(serviceLevelStatus.subscription)
  )
}

// undefined = trial eligibility not yet determined; the Plus row shows a CTA
// skeleton rather than guessing between try and get (ADR 0023).
export function resolvePlusState(
  serviceLevelStatus: ServiceLevelStatus,
  trialEligible: boolean | undefined,
): PassivePlusState | undefined {
  if (isInSubscription(serviceLevelStatus)) return 'active'
  if (trialEligible === undefined) return undefined
  return trialEligible ? 'try' : 'get'
}

interface ItemTypesResponse {
  item_types: ItemType[]
}

interface OwnPurchaseStatsResponse {
  stats: { total_count: number }
}

type BootstrapDependency =
  | 'collections'
  | 'current_user'
  | 'delivery_item_types'
  | 'service_level'
  | 'unlocked_land_skins'
  | 'unlocked_trees'

export class ShopBootstrapError extends Error {
  constructor(
    readonly label: BootstrapDependency,
    readonly detail: string,
  ) {
    super(`${label}: ${detail}`)
    this.name = 'ShopBootstrapError'
  }
}

// Shared by every fail-closed fetch label: bootstrap's dependency labels and
// the daily-pick rollover's attempt boundary.
export function errorDetail(error: unknown): string {
  if (isHTTPError(error)) return String(error.response.status)
  if (error instanceof Error) return error.message
  return String(error)
}

// step names the dependency on the perf timeline; it defaults to the label
// and only differs when one label covers two waits.
async function required<T>(
  label: BootstrapDependency,
  task: Promise<T>,
  step: string = label,
): Promise<T> {
  try {
    return await timed(`bootstrap:${step}`, task)
  } catch (error) {
    if (error instanceof ShopBootstrapError) throw error
    throw new ShopBootstrapError(label, errorDetail(error))
  }
}

function indexCollections(response: ShopProductCollection[]): ShopCollections {
  const byCode = new Map(
    response.map((collection) => [collection.code, collection]),
  )
  for (const code of SHOP_COLLECTION_CODES) {
    if (!byCode.has(code)) {
      throw new ShopBootstrapError('collections', `missing ${code}`)
    }
  }
  const collections = Object.fromEntries(
    SHOP_COLLECTION_CODES.map((code) => [code, byCode.get(code)!]),
  ) as ShopCollections
  for (const code of OPTIONAL_SHOP_COLLECTION_CODES) {
    const collection = byCode.get(code)
    if (collection) collections[code] = collection
  }
  return collections
}

function distinctItemTypeIds(collections: ShopProductCollection[]): number[] {
  const ids = new Set<number>()
  for (const collection of collections) {
    const entries = collection.item_type_product_collection_products
    if (!Array.isArray(entries)) continue
    for (const entry of entries as unknown[]) {
      if (typeof entry !== 'object' || entry === null) continue
      const product = (entry as Record<string, unknown>).item_type_product
      if (typeof product !== 'object' || product === null) continue
      const itemTypeId = (product as Record<string, unknown>).item_type_id
      if (typeof itemTypeId === 'number') ids.add(itemTypeId)
    }
    if (collection.code === 'shop-gem') {
      for (const id of firstPurchaseBonusItemTypeIds(entries)) ids.add(id)
    }
  }
  return [...ids]
}

// The bonus item types a First-purchase bonus names ride the same by-ids
// request as the collection's own item types (GLOSSARY.md "First-purchase
// bonus (首購翻倍)"). Malformed offers contribute nothing here; currency
// hydration reports them.
function firstPurchaseBonusItemTypeIds(entries: unknown[]): number[] {
  const ids: number[] = []
  for (const entry of entries) {
    if (!isRecord(entry) || !Array.isArray(entry.offers)) {
      continue
    }
    const offer = readFirstPurchaseOffer(entry.offers)
    if (offer.kind === 'eligible') {
      ids.push(offer.bonusItemTypeId)
    }
  }
  return ids
}

function fetchItemTypesByIds(ids: number[]): Promise<ItemTypesResponse> {
  return api.service
    .get(`delivery/item-types/by-ids?${ids.map((id) => `ids=${id}`).join('&')}`)
    .json<ItemTypesResponse>()
}

function deployEnvironment(): 'production' | 'staging' | 'dev' {
  if (import.meta.env.MODE === 'production') return 'production'
  if (import.meta.env.MODE === 'staging') return 'staging'
  return 'dev'
}

async function loadTrialEligibility(userId: number): Promise<boolean> {
  if (shouldUseMockAPI) {
    // pending-top-up holds the Plus CTA skeleton along with the sections.
    if (mockScenarioConfig.topUpHydration === 'pending') {
      return new Promise<boolean>(() => {})
    }
    return mockScenarioConfig.trialEligible
  }
  if (platform === 'china_android') {
    const productId = getChinaAndroidTrialProductId(deployEnvironment())
    if (productId === undefined) {
      throw new Error('China Android trial product is not configured')
    }
    const response = await api.service
      .get(`store/users/${userId}/product/${productId}/own-purchase-stats`)
      .json<OwnPurchaseStatsResponse>()
    return response.stats.total_count === 0
  }

  const products: Products = await getProducts({
    api,
    appVersion: resolvedAppVersion,
    cacheKeyPrefix: 'shop.products.v1',
    deferredUserInfo: userInfoSource.getReady(),
    env: deployEnvironment(),
    platform,
    shouldMock: shouldUseMockAPI,
  })
  return products.annualEarlyBird.eligible_trial_days > 0
}

function loadServiceLevelStatus(userId: number): Promise<ServiceLevelStatus> {
  return api.service
    .get(`user/users/${userId}/service-levels/status`)
    .json<ServiceLevelStatus>()
}

export async function refreshServiceLevelAfterPaywall(
  userId: number,
): Promise<ServiceLevelStatus> {
  try {
    return await loadServiceLevelStatus(userId)
  } catch (error) {
    throw new Error(`service_level_after_paywall: ${errorDetail(error)}`)
  }
}

export async function loadShopBootstrap(): Promise<ShopBootstrap> {
  const userInfo = await required(
    'current_user',
    userInfoSource.getReady(),
    'user_info',
  )
  const collectionQuery = [
    ...SHOP_COLLECTION_CODES,
    ...OPTIONAL_SHOP_COLLECTION_CODES,
  ]
    .map((code) => `codes=${code}`)
    .join('&')

  const [
    currentUser,
    collectionResponse,
    unlockedTrees,
    unlockedLandSkins,
    serviceLevelStatus,
  ] = await Promise.all([
    required(
      'current_user',
      api.service.get('user/users/current').json<CurrentUser>(),
    ),
    required(
      'collections',
      api.service
        .get(
          `store/users/${userInfo.user_id}/product-collections?${collectionQuery}`,
        )
        .json<ShopProductCollection[]>(),
    ),
    required(
      'unlocked_trees',
      api.service
        .get('user/tree_types/unlocked_timestamps')
        .json<UnlockedTreeTimestamp[]>(),
    ),
    required(
      'unlocked_land_skins',
      api.service
        .get('user/land-skins/unlocked-timestamps')
        .json<UnlockedLandSkinTimestamp[]>(),
    ),
    required('service_level', loadServiceLevelStatus(userInfo.user_id)),
  ])

  const collections = indexCollections(collectionResponse)
  const itemTypeIds = distinctItemTypeIds(Object.values(collections))
  const itemTypes =
    itemTypeIds.length === 0
      ? []
      : (
          await required(
            'delivery_item_types',
            fetchItemTypesByIds(itemTypeIds),
          )
        ).item_types

  return {
    collections,
    currentUser,
    itemTypes,
    serviceLevelStatus,
    unlockedLandSkins,
    unlockedTrees,
  }
}

export interface DailyPickRollover {
  collection: ShopProductCollection
  itemTypes: ItemType[]
}

// The daily-pick rollover's refetch (ADR 0028): Shop's one mid-session
// refetch — the daily-pick collection alone, plus the item types its new
// assortment needs. A response without the collection is a failure like any
// other; the rollover coordinator labels every failure with the one
// rollover-identifying general-error code at its attempt boundary.
export async function loadDailyPickRollover(
  userId: number,
): Promise<DailyPickRollover> {
  const response = await api.service
    .get(`store/users/${userId}/product-collections?codes=shop-daily-pick`)
    .json<ShopProductCollection[]>()
  const collection = response.find((entry) => entry?.code === 'shop-daily-pick')
  if (!collection) throw new Error('missing shop-daily-pick')
  const itemTypeIds = distinctItemTypeIds([collection])
  if (itemTypeIds.length === 0) return { collection, itemTypes: [] }
  const { item_types } = await fetchItemTypesByIds(itemTypeIds)
  return { collection, itemTypes: item_types }
}

export interface StarterPackRefetch {
  // undefined = the response held no shop-starter-pack member: no starter
  // pack, never a failure (ADR 0044).
  collection?: ShopProductCollection
  // Only the item types Shop did not hold yet.
  itemTypes: ItemType[]
}

// The starter-pack refetch after a committed purchase (GLOSSARY.md "Starter
// pack (新手禮包)"): the shop-starter-pack collection alone, plus whichever of
// its item types are not in knownItemTypeIds. Throws on any failed request;
// the caller keeps the current state.
export async function loadStarterPackRefetch(
  userId: number,
  knownItemTypeIds: ReadonlySet<number>,
): Promise<StarterPackRefetch> {
  const response = await api.service
    .get(`store/users/${userId}/product-collections?codes=shop-starter-pack`)
    .json<ShopProductCollection[]>()
  if (!Array.isArray(response)) throw new Error('invalid collections response')
  const collection = response.find(
    (entry) => entry?.code === 'shop-starter-pack',
  )
  if (!collection) return { itemTypes: [] }
  const missing = distinctItemTypeIds([collection]).filter(
    (id) => !knownItemTypeIds.has(id),
  )
  if (missing.length === 0) return { collection, itemTypes: [] }
  const { item_types } = await fetchItemTypesByIds(missing)
  return { collection, itemTypes: item_types }
}

export const shopBootstrapQuery = () => ({
  queryKey: ['shop', 'bootstrap'] as const,
  queryFn: () => timed('bootstrap', loadShopBootstrap),
  gcTime: Infinity,
  retry: false,
  staleTime: Infinity,
})

// Off the bootstrap critical path (ADR 0023). The caller enables this only
// with the native paywall capability and for a non-subscriber; the Plus row
// shows a CTA skeleton while it resolves. Unlike the bootstrap query it
// retries: a terminal failure hides the Plus row instead of opening the general error dialog, so
// retrying costs nothing visible and can save the upsell exposure.
export const trialEligibilityQuery = (
  bootstrap: Pick<ShopBootstrap, 'currentUser'>,
) => ({
  queryKey: ['shop', 'trial-eligibility'] as const,
  queryFn: () => loadTrialEligibility(bootstrap.currentUser.id),
  gcTime: Infinity,
  retry: 2,
  staleTime: Infinity,
})
